import React from 'react';
import { Chip, Tooltip } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import LinkOffIcon from '@mui/icons-material/LinkOff';

const platformNames = {
  square: 'Square',
  shopify: 'Shopify',
  etsy: 'Etsy',
};

const formatSyncTime = (value) => {
  if (!value) return null;
  let date;
  if (value._seconds != null) {
    date = new Date(value._seconds * 1000);
  } else if (typeof value === 'number') {
    date = new Date(value);
  } else {
    date = new Date(value);
  }
  if (isNaN(date.getTime())) return null;
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays}d ago`;
};

const getStatusConfig = (status) => {
  if (!status || !status.status || status.status === 'disconnected') {
    return {
      label: 'Not Connected',
      color: 'default',
      variant: 'outlined',
      icon: <LinkOffIcon />,
      tooltip: null,
    };
  }

  const name = platformNames[status.platform] || status.platform || 'Platform';
  const merchantName = status.platformMetadata?.merchantName;

  switch (status.status) {
    case 'connected': {
      const syncInfo = formatSyncTime(status.lastSyncAt);
      return {
        label: `Connected to ${name}`,
        color: 'success',
        variant: 'filled',
        icon: <CheckCircleIcon />,
        tooltip: [
          merchantName && `Merchant: ${merchantName}`,
          syncInfo && `Last sync: ${syncInfo}`,
          status.lastSyncStatus && `Sync status: ${status.lastSyncStatus}`,
        ].filter(Boolean).join(' | ') || null,
      };
    }
    case 'token_expired':
      return {
        label: 'Connection Expired',
        color: 'warning',
        variant: 'outlined',
        icon: <WarningIcon />,
        tooltip: `${name} connection needs to be re-authorized`,
      };
    case 'error':
      return {
        label: 'Connection Error',
        color: 'error',
        variant: 'outlined',
        icon: <ErrorIcon />,
        tooltip: `${name} connection has an error. Try reconnecting.`,
      };
    case 'pending_authorization':
      return {
        label: 'Connecting...',
        color: 'info',
        variant: 'outlined',
        icon: <HourglassEmptyIcon />,
        tooltip: `Waiting for ${name} authorization`,
      };
    default:
      return {
        label: 'Not Connected',
        color: 'default',
        variant: 'outlined',
        icon: <LinkOffIcon />,
        tooltip: null,
      };
  }
};

const IntegrationStatus = ({ status }) => {
  const config = getStatusConfig(status);

  const chip = (
    <Chip
      label={config.label}
      color={config.color}
      variant={config.variant}
      icon={config.icon}
      size="small"
      aria-label={`Integration status: ${config.label}`}
    />
  );

  if (config.tooltip) {
    return (
      <Tooltip title={config.tooltip} arrow>
        {chip}
      </Tooltip>
    );
  }

  return chip;
};

export default IntegrationStatus;
